export const kotlinDefaultSource = `package com.example.modernization

// Example class with Java-style null checking patterns
class UserManager(private val userService: UserService) {
    
    fun getUserName(userId: String): String {
        // Java-style null checking
        val user = userService.findUserById(userId)
        if (user == null) {
            return "Unknown User"
        }
        return user.name
    }
    
    fun getUserEmail(userId: String): String {
        val user = userService.findUserById(userId)
        if (user != null) {
            val email = user.getEmail()
            if (email != null) {
                return email
            } else {
                return "No email available"
            }
        }
        return "User not found"
    }
    
    fun getFullAddress(userId: String): String {
        val user = userService.findUserById(userId)
        if (user == null) return "No address found"
        
        val address = user.getAddress()
        if (address == null) return "No address found"
        
        val street = address.getStreet()
        val city = address.getCity()
        val zip = address.getZipCode()
        
        if (street == null || city == null || zip == null) {
            return "Incomplete address"
        }
        
        return "$street, $city, $zip"
    }
    
    // Already using Kotlin idioms
    fun getUserProfile(userId: String): Profile {
        val user = userService.findUserById(userId) ?: return Profile("Unknown", "")
        val email = user.getEmail() ?: "No email"
        
        return Profile(user.name, email)
    }
}

class Profile(val name: String, val email: String)

interface UserService {
    fun findUserById(id: String): User?
}

interface User {
    val name: String
    fun getEmail(): String?
    fun getAddress(): Address?
}

interface Address {
    fun getStreet(): String?
    fun getCity(): String?
    fun getZipCode(): String?
}`;

export const kotlinDefaultCodemod = `import { type SgRoot } from "@ast-grep/napi";

async function transform(root: SgRoot): Promise<string | null> {
  const rootNode = root.root();

  // Find simple null checks followed by return
  const simpleNullChecks = rootNode.findAll({
    rule: {
      pattern: "if ($VAR == null) { return $DEFAULT }"
    }
  });

  let edits = simpleNullChecks.map(node => {
    const variableName = node.getMatch("VAR").text();
    const defaultValue = node.getMatch("DEFAULT").text();
    return node.replace(\`val \${variableName}Safe = \${variableName} ?: return \${defaultValue}\`);
  });

  // Find negated null checks with nested operations
  const negatedNullChecks = rootNode.findAll({
    rule: {
      pattern: \`
        if ($VAR != null) {
          $BODY
        } else {
          return $DEFAULT
        }
      \`
    }
  });

  edits = [
    ...edits,
    ...negatedNullChecks.map(node => {
      const variableName = node.getMatch("VAR").text();
      const body = node.getMatch("BODY").text();
      const defaultValue = node.getMatch("DEFAULT").text();
      return node.replace(\`val \${variableName}Safe = \${variableName} ?: return \${defaultValue}\n\${body}\`);
    })
  ];

  // Find inline null checks
  const inlineNullChecks = rootNode.findAll({
    rule: {
      pattern: "if ($VAR == null) return $DEFAULT"
    }
  });

  edits = [
    ...edits,
    ...inlineNullChecks.map(node => {
      const variableName = node.getMatch("VAR").text();
      const defaultValue = node.getMatch("DEFAULT").text();
      return node.replace(\`val \${variableName}Safe = \${variableName} ?: return \${defaultValue}\`);
    })
  ];

  // Apply all changes
  const newSource = rootNode.commitEdits(edits);
  return newSource;
}

export default transform;
`;

// Convoluted nonsense required to append to a variable name, see:
// https://ast-grep.github.io/guide/rewrite/transform.html#multiple-sequential-transformations
export const kotlinDefaultConfig = `
# Kotlin null safety modernization
id: single-if-statement
language: kotlin
rule:
  any:
    - pattern: "if ($VAR == null) { return $DEFAULT }"
    - pattern: "if ($VAR == null) return $DEFAULT"
transform:
  SAFEVARSEP:
    replace:
      by: $var-Safe
      replace: (?<var>.*)
      source: $VAR
  SAFEVAR:
    convert:
      toCase: camelCase
      source: $SAFEVARSEP
fix: "val $SAFEVAR = $VAR ?: return $DEFAULT"
---
id: if-else-statement
language: kotlin
rule:
  pattern: |
    if ($VAR != null) {
      $BODY
    } else {
      return $DEFAULT
    }
transform:
  SAFEVARSEP:
    replace:
      by: $var-Safe
      replace: (?<var>.*)
      source: $VAR
  SAFEVAR:
    convert:
      toCase: camelCase
      source: $SAFEVARSEP
fix: |
  val $SAFEVAR = $VAR ?: return $DEFAULT
  $BODY
`.trim();
